import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "@mui/material";
import { Modal } from "react-bootstrap";

import styles from "./others.module.css";

import chatPlaceholder from "../../../../assets/images/chatPlaceholder.svg";

import * as matchAction from "../../../../actions/match";
import { getProfileImageUrl, resolveProfileId } from "../../../../utils/serverUtils";

export default function Others() {
  const navigate = useNavigate();
  const { type } = useParams();

  const [loading, setLoading] = useState(true);

  const [text, setText] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [selectedPersonId, setSelectedPersonId] = useState(null);

  const [showUnblockModal, setUnblockModal] = useState(false);
  const [showUnreportModal, setUnreportModal] = useState(false);
  const [showUnmatchedModal, setUnmatchedModal] = useState(false);

  useEffect(() => {
    if (!type) return;

    setProfiles([]);
    setLoading(true);

    fetchListByType(type);
  }, [type]);

  const fetchListByType = async (type) => {
    try {
      let result = [];
      if (type === "skipped") {
        setText("You haven’t skipped any members");
        result = await matchAction.getSkippedList();
      } else if (type === "blocked") {
        setText("You haven’t blocked any members");
        result = await matchAction.getBlockedList();
      } else if (type === "reported") {
        setText("You haven’t reported any members");
        result = (await matchAction.getReportedList()).data;
      } else if (type === "unmatched") {
        setText("You haven’t unmatched any members");
        result = (await matchAction.getUnmatched()).data;
      }
      setProfiles(result);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = (profile) => {
    if (type === "blocked") {
      setSelectedPersonId(profile.user_id);
      setUnblockModal(true);
    } else if (type === "reported") {
      setSelectedPersonId(profile.user_id);
      setUnreportModal(true);
    } else if (type === "unmatched") {
      setSelectedPersonId(profile.unmatch_id);
      setUnmatchedModal(true);
    } else {
      navigate(
        `/user/${resolveProfileId(profile)}?token=${encodeURIComponent(
          profile.profile_token || ""
        )}`,
        {
          state: {
            profile_token: profile.profile_token,
          },
        }
      );
    }
  };

  return (
    <>
      <ul className={styles.nav}>
        <li
          onClick={(e) => {
            e.preventDefault();
            navigate("/user/chat/others/skipped");
          }}
          className={type === "skipped" ? styles.active : ""}
        >
          <p>Skipped</p>
          <div className={styles.indicator}></div>
        </li>
        <li
          onClick={(e) => {
            e.preventDefault();
            navigate("/user/chat/others/blocked");
          }}
          className={type === "blocked" ? styles.active : ""}
        >
          <p>Blocked</p>
          <div className={styles.indicator}></div>
        </li>
        <li
          onClick={(e) => {
            e.preventDefault();
            navigate("/user/chat/others/reported");
          }}
          className={type === "reported" ? styles.active : ""}
        >
          <p>Reported</p>
          <div className={styles.indicator}></div>
        </li>
        <li
          onClick={(e) => {
            e.preventDefault();
            navigate("/user/chat/others/unmatched");
          }}
          className={type === "unmatched" ? styles.active : ""}
        >
          <p>Unmatched</p>
          <div className={styles.indicator}></div>
        </li>
      </ul>

      {Array.isArray(profiles) && !loading && profiles.length === 0 && (
        <div className={styles.chatPlaceholder}>
          <img src={chatPlaceholder} alt="chat placeholder" />
          <p>{text}</p>
        </div>
      )}

      <div className={styles.profiles}>
        {loading &&
          Array(6)
            .fill(0)
            .map((_, i) => (
              <Skeleton
                key={i}
                animation="pulse"
                sx={{
                  borderRadius: "12px",
                }}
                variant="rounded"
                width={160}
                height={100}
              />
            ))}

        {Array.isArray(profiles) &&
          profiles.map((profile, i) => (
            <div
              key={i}
              style={{
                backgroundImage: `url(${getProfileImageUrl(profile)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
              onClick={() => handleProfileClick(profile)}
            >
              <div style={{ width: "100%" }}>
                <p>{profile.first_name}</p>
                {profile.location_string ? (
                  <p>
                    {profile.location_string}, {profile.country}
                  </p>
                ) : (
                  <p>{profile.country}</p>
                )}
              </div>
            </div>
          ))}
      </div>

      <UnblockModal
        show={showUnblockModal}
        setShow={setUnblockModal}
        personId={selectedPersonId}
      />

      <UnreportModal
        show={showUnreportModal}
        setShow={setUnreportModal}
        personId={selectedPersonId}
      />

      <UnmatchModal
        show={showUnmatchedModal}
        setShow={setUnmatchedModal}
        personId={selectedPersonId}
      />
    </>
  );
}

function UnblockModal({ show, setShow, personId }) {
  const navigate = useNavigate();
  const handleUnblock = async () => {
    try {
      await matchAction.unblockUser(personId);
      navigate("/user/chat/matches");
    } catch (error) {
      console.error("Unblock failed", error);
    }
  };

  return (
    <Modal
      className="customer-modal-without-header-footer"
      size="sm"
      centered
      show={show}
    >
      <Modal.Body>
        <p
          style={{
            fontSize: "20px",
            fontWeight: "600",
            margin: "0",
            marginBottom: "1rem",
            color: "#6c6c6c",
          }}
        >
          Unblock user
        </p>
        <p
          style={{
            fontSize: "16px",
            margin: "0",
            textAlign: "center",
            color: "#6c6c6c",
          }}
        >
          Unblocking this member will allow them to message you and show up when
          you're browsing.
        </p>
        <div
          style={{
            marginTop: "3rem",
            display: "flex",
            gap: "1rem",
            marginInline: "auto",
          }}
        >
          <button
            type="button"
            className="global-cancel-button"
            onClick={() => setShow(false)}
          >
            Close
          </button>
          <button onClick={handleUnblock} className="global-save-button">
            Submit
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function UnreportModal({ show, setShow, personId }) {
  const navigate = useNavigate();
  const handleUnreport = async () => {
    try {
      await matchAction.unReportUser(personId);
      navigate("/user/chat/matches");
    } catch (error) {
      console.error("Unreport failed", error);
    }
  };

  return (
    <Modal
      className="customer-modal-without-header-footer"
      size="sm"
      centered
      show={show}
    >
      <Modal.Body>
        <p
          style={{
            fontSize: "20px",
            fontWeight: "600",
            margin: "0",
            marginBottom: "1rem",
            color: "#6c6c6c",
          }}
        >
          Remove Report
        </p>
        <p
          style={{
            fontSize: "16px",
            margin: "0",
            textAlign: "center",
            color: "#6c6c6c",
          }}
        >
          Removing this report will allow this member to interact with you again
          on the platform.
        </p>
        <div
          style={{
            marginTop: "3rem",
            display: "flex",
            gap: "1rem",
            marginInline: "auto",
          }}
        >
          <button
            type="button"
            className="global-cancel-button"
            onClick={() => setShow(false)}
          >
            Close
          </button>
          <button onClick={handleUnreport} className="global-save-button">
            Submit
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function UnmatchModal({ show, setShow, personId }) {
  const navigate = useNavigate();

  const handleUnmatch = async () => {
    try {
      await matchAction.deleteUnmatched(personId);
      navigate("/user/chat/matches");
    } catch (error) {
      console.error("Unmatch failed", error);
    }
  };

  return (
    <Modal
      className="customer-modal-without-header-footer"
      size="sm"
      centered
      show={show}
    >
      <Modal.Body>
        <p
          style={{
            fontSize: "20px",
            fontWeight: "600",
            margin: "0",
            marginBottom: "1rem",
            color: "#6c6c6c",
          }}
        >
          Undo Unmatch
        </p>
        <p
          style={{
            fontSize: "16px",
            margin: "0",
            textAlign: "center",
            color: "#6c6c6c",
          }}
        >
          Undoing this unmatch will allow this member to interact with you again
          on the platform.
        </p>
        <div
          style={{
            marginTop: "3rem",
            display: "flex",
            gap: "1rem",
            marginInline: "auto",
          }}
        >
          <button
            type="button"
            className="global-cancel-button"
            onClick={() => setShow(false)}
          >
            Close
          </button>
          <button onClick={handleUnmatch} className="global-save-button">
            Submit
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
